import { createContext, ReactNode, useContext } from 'react';

export interface CadConfig {
  enabled: boolean;
  loginUrl: string;
  clientId: string;
  scopes: string;
  /**
   * Where the browser is redirected after a CAD sign-out. CAD has no OIDC
   * end-session endpoint, so logout is local-only — we just clear
   * oidc-client-ts storage and navigate here. Defaults to
   * `window.location.origin` when unset.
   */
  postLogoutRedirectUri?: string;
}

const DEFAULT_CONFIG: CadConfig = { enabled: false, loginUrl: '', clientId: '', scopes: '' };

let _config: CadConfig | null = null;
let _loading: Promise<CadConfig> | null = null;

/**
 * Resolve the CAD config URL from the preload <link id="cad-config"> injected
 * by the host HTML. Falls back to a root-relative path when the link is
 * absent.
 */
function resolveCadConfigUrl(): string {
  const link = document.getElementById('cad-config') as HTMLLinkElement | null;
  return link?.href || '/cad.appsettings.json';
}

/**
 * Fetch cad.appsettings.json once per session and cache the result. Call at
 * app startup (awaited before mount) so downstream synchronous getters
 * (`getCadConfig`, `isCadAuthEnabled`) return the real config on first render.
 */
export function loadCadConfig(): Promise<CadConfig> {
  if (_config) return Promise.resolve(_config);
  if (_loading) return _loading;
  _loading = (async () => {
    const url = resolveCadConfigUrl();
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      _config = (await res.json()) as CadConfig;
    } catch (err) {
      console.error(
        `[CAD] Failed to load cad.appsettings.json from ${url}, using defaults:`,
        err,
      );
      _config = DEFAULT_CONFIG;
    }
    return _config;
  })();
  return _loading;
}

/** Return the cached CAD config or defaults when `loadCadConfig` has not resolved yet. */
export function getCadConfig(): CadConfig {
  return _config ?? DEFAULT_CONFIG;
}

/** Test-only hook: override the in-memory config without triggering a fetch. */
export function __setCadConfigForTesting(config: CadConfig | null): void {
  _config = config;
  _loading = null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CadConfigContext = createContext<CadConfig>(DEFAULT_CONFIG);

export function useCadConfig(): CadConfig {
  return useContext(CadConfigContext);
}

interface CadConfigProviderProps {
  /** Override the loginUrl from cad.appsettings.json (e.g., staff URL for Security-UI) */
  loginUrl?: string;
  children: ReactNode;
}

/**
 * Provides the CAD config to the tree via context. Reads the in-memory
 * singleton populated by `loadCadConfig()` — await that at startup.
 */
export function CadConfigProvider({ loginUrl, children }: CadConfigProviderProps) {
  const base = getCadConfig();
  const config = loginUrl ? { ...base, loginUrl } : base;
  return <CadConfigContext.Provider value={config}>{children}</CadConfigContext.Provider>;
}
